'use client';

import { useMemo, useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { PostSummary } from '@/lib/mdx';

interface RelatedPostsProps {
  currentSlug: string;
  tags?: string[];
  categories?: string[];
  allPosts: PostSummary[];
}

export function RelatedPosts({
  currentSlug,
  tags = [],
  categories = [],
  allPosts
}: RelatedPostsProps) {
  const [isBlogSubdomain, setIsBlogSubdomain] = useState(false);

  useEffect(() => {
    const host = window.location.hostname;
    if (
      host.includes('blog.bioinfometrics.com') ||
      host.includes('blog.localhost')
    ) {
      setIsBlogSubdomain(true);
    }
  }, []);

  const blogPath = isBlogSubdomain ? '' : '/blog';

  const relatedPosts = useMemo(() => {
    // Filter out the current post
    const otherPosts = allPosts.filter((post) => post.slug !== currentSlug);

    // Calculate match score for each post based on overlapping tags and categories
    const postsWithScores = otherPosts.map((post) => {
      let score = 0;
      
      const postTags = post.meta.tags || [];
      const postCategories = post.meta.categories || [];

      // Check tags overlap
      tags.forEach((tag) => {
        if (postTags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
          score += 2; // Tags match gets higher weight
        }
      });

      // Check categories overlap
      categories.forEach((cat) => {
        if (postCategories.some((c) => c.toLowerCase() === cat.toLowerCase())) {
          score += 1;
        }
      });

      return { post, score };
    });

    // Sort by score (descending), then by date (newest first)
    postsWithScores.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return new Date(b.post.meta.date).getTime() - new Date(a.post.meta.date).getTime();
    });

    // Take top 3
    let results = postsWithScores.slice(0, 3).map((x) => x.post);

    // If we have fewer than 3 related posts, backfill with newest posts
    if (results.length < 3) {
      const existingSlugs = new Set(results.map((p) => p.slug));
      const backfill = otherPosts
        .filter((post) => !existingSlugs.has(post.slug))
        .sort((a, b) => new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime())
        .slice(0, 3 - results.length);
      
      results = [...results, ...backfill];
    }

    return results;
  }, [currentSlug, tags, categories, allPosts]);

  if (relatedPosts.length === 0) return null;

  return (
    <section className='border-border mt-16 border-t pt-10 pb-4'>
      <h2 className='font-serif text-2xl font-bold tracking-tight mb-8'>
        Recommended Reading
      </h2>
      <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
        {relatedPosts.map((post) => {
          const mainTag = post.meta.tags?.[0] || post.meta.categories?.[0] || 'Article';
          
          return (
            <Link
              key={post.slug}
              href={`${blogPath}/${post.slug}`}
              className='group flex flex-col overflow-hidden rounded-2xl border border-border/30 bg-card/40 transition-all duration-300 hover:-translate-y-1 hover:border-border/80 hover:shadow-md hover:bg-card'>
              
              {/* Thumbnail Container */}
              {post.meta.image ? (
                <div className='relative aspect-[16/10] w-full overflow-hidden bg-muted'>
                  <Image
                    src={post.meta.image.src}
                    alt={post.meta.image.alt || post.meta.title}
                    fill
                    className='object-cover transition-transform duration-500 group-hover:scale-105'
                    sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                  />
                  {post.meta.premium && (
                    <span className='absolute top-3 right-3 bg-primary text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full shadow-sm'>
                      Premium
                    </span>
                  )}
                </div>
              ) : (
                <div className='flex aspect-[16/10] w-full items-center justify-center bg-primary/5 text-primary/40'>
                  <span className='text-sm font-semibold uppercase tracking-wider'>{mainTag}</span>
                </div>
              )}

              {/* Card Body */}
              <div className='flex flex-1 flex-col p-5'>
                {/* Meta details */}
                <div className='flex items-center gap-2 mb-2'>
                  <span className='text-primary text-[10px] font-bold tracking-wider uppercase bg-primary/10 px-2.5 py-0.5 rounded-full'>
                    {mainTag}
                  </span>
                </div>

                {/* Title */}
                <h3 className='font-serif text-base font-bold leading-snug text-foreground/90 line-clamp-2 group-hover:text-primary transition-colors mb-3'>
                  {post.meta.title}
                </h3>

                {/* Footer Meta */}
                <div className='mt-auto flex items-center justify-between text-[11px] text-muted-foreground/60 font-medium'>
                  <span>
                    {new Date(post.meta.date).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                  {post.meta.readingTime && (
                    <span>{post.meta.readingTime}</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
